/* eslint-disable @reatom/unit-naming-rule */
import { Result } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'
import { reatomResource } from '@root/shared/lib'

/**
 * What is trading right now, for the front door.
 *
 * The door had the mark on an empty field, which says nothing a visitor could
 * not guess. The strongest thing to put in front of somebody deciding whether
 * to ask for an invitation is the product's own data: these are real tokens,
 * read a moment ago.
 *
 * `pools/ticker` rather than the feed it is drawn from. Measured against
 * staging: the trending feed answers with whole pool cards — **95,600 bytes
 * for eight tokens** — where this answers with the six fields a row can draw,
 * **3,926 for twelve**. Twenty-four times smaller, for a screen nobody has
 * signed in to yet.
 *
 * Signed out on purpose, and it works: the `/services` feeds take no cookie.
 * `pair-info` does — it answers 404 without one — so there is no deeper
 * figure to show on hover than what arrives here, and the row does not
 * pretend otherwise.
 *
 * Read once. A door is not a dashboard, and polling a screen somebody is
 * about to leave is a request for nothing.
 */

/** As many as the column shows; asking for more is bytes nobody draws. */
const SHOWN = 9

export interface DoorToken {
  tokenAddress: string
  symbol: string
  /** Empty when the token has no picture; the row draws a letter. */
  logo: string
  marketCapUsd: string
  /** Over a day, which is the window the ticker carries. */
  priceChangePercents: string
}

export const doorTokensStateAtom = reatomResource(
  async (ctx) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.services.servicesPoolsTickerCreate, [
        {
          network: Network.toDto(Network.Solana),
          limit: SHOWN,
          // Codex's ranking, the same one the explore board asks for. Our own
          // screens read the last hour of swaps, so they answer with nothing
          // whenever ingestion is behind — and a door that goes blank because
          // an indexer is catching up is worse than one ranked by somebody
          // else. Verified: without this the route answers `[]`, which is how
          // the board shipped empty.
          strategy: 'codex',
        },
      ]),
    )

    return Result.map(
      Result.mapError(result, ApiError.fallbackToUnknown),
      (rows): ReadonlyArray<DoorToken> =>
        rows.map((row) => ({
          tokenAddress: row.tokenAddress,
          symbol: row.symbol,
          logo: row.logo,
          marketCapUsd: row.marketCapUsd,
          priceChangePercents: row.priceChangePercents,
        })),
    )
  },
  { name: 'doorTokensStateAtom', loadOnConnect: true },
)
